import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, FileText, BookOpen, HelpCircle, Heart, Mail, AlertOctagon, Users, Book, Newspaper } from "lucide-react";
import { useAuth, ROLE_LABEL, hasPerm } from "@/lib/auth";
import { T } from "@/constants/testIds";

const NAV = [
  { slug: "dashboard", to: "/cms", label: "Dashboard", icon: LayoutDashboard },
  { slug: "articles", to: "/cms/articles", label: "Articles", icon: FileText },
  { slug: "bible-studies", to: "/cms/bible-studies", label: "Bible Studies", icon: Book },
  { slug: "questions", to: "/cms/questions", label: "Questions", icon: HelpCircle },
  { slug: "prayer", to: "/cms/prayer-requests", label: "Prayer Requests", icon: Heart, perm: "moderate" },
  { slug: "messages", to: "/cms/messages", label: "Contact Messages", icon: Mail, perm: "moderate" },
  { slug: "ask", to: "/cms/ask", label: "Ask a Question", icon: Newspaper, perm: "moderate" },
  { slug: "corrections", to: "/cms/corrections", label: "Corrections", icon: AlertOctagon, perm: "moderate" },
  { slug: "users", to: "/cms/users", label: "Users", icon: Users, perm: "manage_users" },
];

export default function CMSLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav("/elder-login");
  };

  return (
    <div className="min-h-screen bg-subtle text-ink flex">
      <aside data-testid={T.cmsSidebar} className="w-64 bg-ink text-background/90 flex flex-col shrink-0">
        <div className="px-6 py-6 border-b border-background/10">
          <Link to="/" className="block">
            <div className="font-serif text-xl text-background" style={{ fontWeight: 500 }}>
              Anant Jeevan
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-gold mt-1">Elder CMS</div>
          </Link>
        </div>
        <nav className="flex-1 py-4">
          {NAV.filter((n) => !n.perm || hasPerm(user, n.perm)).map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.slug}
                to={n.to}
                end={n.to === "/cms"}
                data-testid={T.cmsNav(n.slug)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-2.5 text-sm border-l-2 ${
                    isActive
                      ? "bg-background/5 border-gold text-background"
                      : "border-transparent text-background/70 hover:text-background hover:bg-background/5"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="px-6 py-4 border-t border-background/10">
          <div data-testid={T.cmsUserBadge} className="text-xs text-background/60">
            {user?.name}
            <div className="text-[10px] uppercase tracking-widest text-gold">{ROLE_LABEL[user?.role] || user?.role}</div>
          </div>
          <button
            data-testid={T.cmsLogout}
            onClick={handleLogout}
            className="mt-3 flex items-center gap-2 text-xs uppercase tracking-widest text-background/80 hover:text-gold"
          >
            <LogOut className="h-3.5 w-3.5" /> Log out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <div className="px-8 py-8 max-w-[1200px]">{children}</div>
      </main>
    </div>
  );
}
